import { useEffect, useState } from 'react'
import { AlertTriangle, X, RotateCcw, Trash2 } from 'lucide-react'
import {
  onSyncEvent,
  getFailedTransactions,
  discardFailedTransaction,
  retryFailedTransaction,
  syncAllPending,
  type PendingTransaction,
} from '../lib/offlineQueue'
import { idb, STORES } from '../lib/idb'

/**
 * Look up a customer name from the IDB cache for a queued transaction.
 * Falls back to "Unknown customer" if the customer isn't in cache (e.g.,
 * was added during this session before any cache refresh). Used so the
 * failed-queue banner shows "Sale to Al Noor Grocery" instead of an
 * opaque "sale  POST /sales".
 */
async function describeTransaction(tx: PendingTransaction): Promise<string> {
  const body = tx.body as { customer_id?: string; supplier_id?: string } | null | undefined
  try {
    if (body?.customer_id) {
      const c = await idb.get<{ name?: string }>(STORES.CUSTOMERS, body.customer_id)
      if (c?.name) return c.name
    }
    if (body?.supplier_id) {
      const s = await idb.get<{ name?: string }>(STORES.SUPPLIERS, body.supplier_id)
      if (s?.name) return s.name
    }
  } catch {
    // IDB read failure — fall through to generic label.
  }
  return tx.type === 'sale' || tx.type === 'payment' ? 'Unknown customer' : 'Unknown party'
}

/**
 * Surfaces transactions that the offline sync gave up on (3+ retries or
 * non-retryable 4xx response). Without this banner the only signal that a
 * sale or payment never made it to the server was a silent inventory
 * restoration — easy to miss until end-of-day reconciliation.
 *
 * The banner is collapsed by default (just a chip). Tapping it expands a
 * list with Retry / Discard actions per transaction.
 */
interface FailedRow {
  tx: PendingTransaction
  partyName: string
}

export default function FailedQueueBanner() {
  const [failed, setFailed] = useState<FailedRow[]>([])
  const [expanded, setExpanded] = useState(false)

  const refresh = async () => {
    const txs = await getFailedTransactions()
    const rows = await Promise.all(
      txs.map(async (tx) => ({ tx, partyName: await describeTransaction(tx) }))
    )
    setFailed(rows)
  }

  useEffect(() => {
    refresh()
    const unsub = onSyncEvent((e) => {
      if (e.type === 'queue_updated' || e.type === 'sync_completed') refresh()
    })
    return unsub
  }, [])

  if (failed.length === 0) return null

  const handleRetry = async (id: number) => {
    await retryFailedTransaction(id)
    await syncAllPending()
  }

  const handleDiscard = async (id: number) => {
    if (!confirm('Discard this failed transaction? The data will be lost.')) return
    await discardFailedTransaction(id)
  }

  return (
    <div className="fixed top-2 left-2 right-2 z-[55] sm:left-auto sm:right-2 sm:max-w-md">
      <div className="bg-danger-50 border border-danger-200 rounded-xl shadow-md">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left"
        >
          <AlertTriangle size={18} className="text-danger-600 shrink-0" />
          <span className="text-sm font-medium text-danger-700 flex-1">
            {failed.length} transaction{failed.length === 1 ? '' : 's'} failed to sync
          </span>
          <span className="text-xs text-danger-600">{expanded ? 'Hide' : 'Show'}</span>
        </button>

        {expanded && (
          <div className="border-t border-danger-200 max-h-60 overflow-y-auto divide-y divide-danger-100">
            {failed.map(({ tx, partyName }) => {
              const totalFils = (tx.body as { total_amount?: number; amount?: number } | null)?.total_amount
                ?? (tx.body as { amount?: number } | null)?.amount
              return (
                <div key={tx.id} className="p-3 text-sm">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        <span className="capitalize">{tx.type}</span>
                        <span className="text-gray-500"> · </span>
                        <span>{partyName}</span>
                      </p>
                      {typeof totalFils === 'number' && (
                        <p className="text-xs text-gray-500">
                          AED {(totalFils / 100).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0">
                      {new Date(tx.created_at).toLocaleString()}
                    </span>
                  </div>
                  {tx.error && (
                    <p className="text-xs text-danger-600 mb-2 break-words">{tx.error}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => tx.id && handleRetry(tx.id)}
                      className="flex-1 flex items-center justify-center gap-1 py-3 text-sm bg-white border border-danger-300 text-danger-700 rounded-lg hover:bg-danger-100"
                    >
                      <RotateCcw size={12} />
                      Retry
                    </button>
                    <button
                      onClick={() => tx.id && handleDiscard(tx.id)}
                      className="flex-1 flex items-center justify-center gap-1 py-3 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                    >
                      <Trash2 size={12} />
                      Discard
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-1 right-1 p-3 hover:bg-danger-100 rounded-lg"
            aria-label="Collapse"
          >
            <X size={14} className="text-danger-600" />
          </button>
        )}
      </div>
    </div>
  )
}
